import { useState, useEffect, useRef } from "react";
import { MapPin, Search, X, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Structured venue picker with place autocomplete (keyless, OSM Nominatim via
// backend proxy) + manual fallback. Calls onChange(locationString, detailsObj).
export function VenuePicker({ onChange, autoFocus = false }) {
  const [mode, setMode] = useState("search"); // 'search' | 'manual'
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [manual, setManual] = useState({ name: "", address: "" });
  const timer = useRef(null);

  useEffect(() => {
    if (mode !== "search" || selected || q.trim().length < 3) { setResults([]); return; }
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/producer/places/search", { params: { q } });
        setResults(data.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [q, mode, selected]);

  const pick = (r) => {
    setSelected(r);
    setResults([]);
    const label = r.name && !(r.formatted_address || "").startsWith(r.name)
      ? `${r.name}, ${r.formatted_address}`
      : (r.formatted_address || r.name);
    onChange(label, r);
  };

  const clearSelection = () => { setSelected(null); setQ(""); onChange("", null); };

  const updateManual = (patch) => {
    const next = { ...manual, ...patch };
    setManual(next);
    const loc = [next.name, next.address].filter(Boolean).join(" — ");
    onChange(loc, { manual: true, name: next.name || null, formatted_address: next.address || null, provider: "manual" });
  };

  if (mode === "manual") {
    return (
      <div data-testid="venue-manual">
        <Label className="mb-1.5 block text-xs p-muted">Venue name</Label>
        <Input data-testid="manual-venue-name" value={manual.name} onChange={(e) => updateManual({ name: e.target.value })} placeholder="e.g. Private loft" />
        <Label className="mb-1.5 mt-3 block text-xs p-muted">Address (optional)</Label>
        <Input data-testid="manual-venue-address" value={manual.address} onChange={(e) => updateManual({ address: e.target.value })} placeholder="Street, city…" />
        <button type="button" onClick={() => { setMode("search"); onChange("", null); }}
          className="mt-3 flex items-center gap-1.5 text-xs font-semibold p-accent-text">
          <Search className="h-3.5 w-3.5" /> Search for a venue instead
        </button>
      </div>
    );
  }

  if (selected) {
    return (
      <div data-testid="venue-selected" className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 text-emerald-500" />
            <div>
              <p className="text-sm font-semibold">{selected.name}</p>
              <p className="text-xs p-muted">{selected.formatted_address}</p>
            </div>
          </div>
          <button type="button" data-testid="venue-change-btn" onClick={clearSelection} className="p-subtle hover:opacity-70">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="venue-search">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 p-subtle" />
        <Input data-testid="venue-search-input" autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search venue or address…" className="pl-9" />
      </div>
      {loading && <p className="mt-2 text-xs p-subtle">Searching…</p>}
      {results.length > 0 && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-xl p-inset">
          {results.map((r) => (
            <button type="button" key={r.place_id} data-testid={`venue-result-${r.place_id}`} onClick={() => pick(r)}
              className="flex w-full items-start gap-2 border-b p-border-c p-3 text-left last:border-0 hover:opacity-80">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 p-accent-text" />
              <div>
                <p className="text-sm font-medium">{r.name}</p>
                <p className="text-xs p-subtle">{r.formatted_address}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      <button type="button" data-testid="venue-manual-toggle" onClick={() => { setMode("manual"); onChange("", null); }}
        className="mt-3 flex items-center gap-1.5 text-xs font-semibold p-accent-text">
        <Pencil className="h-3.5 w-3.5" /> Enter location manually
      </button>
    </div>
  );
}
